import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  List,
  ListItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCourseYear } from "@/shared/model/edo-course-year.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoSubject } from "@/shared/model/edo-subject.model";
import { IEdoWikiPost, defaultValue } from "@/shared/model/edo-wiki-post.model";
import { EdoEducationalStage } from "@/shared/model/enumerations/edo-educational-stage.model";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { getEntities as getEdoClasseList } from "@/shared/reducers/entities/edo-class.reducer";
import { getEntities as getEdoSubjectList } from "@/shared/reducers/entities/edo-subject.reducer";
import {
  createEntity,
  updateEntity,
} from "@/shared/reducers/entities/edo-wiki-post.reducer";
import axios from "axios";
import "moment/locale/it";
import React, { createRef, useEffect, useState } from "react";
import { Translate, translate } from "react-jhipster";
import { z } from "zod";
import { Controller } from "react-hook-form";
import { Loader2, Paperclip, X } from "lucide-react";
import { cn } from "@/shared/util/cn";

interface EditWikiPostProps {
  wikiPost?: IEdoWikiPost;
  open: boolean;
  onClose: () => void;
}

const EditWikiPost = (props: EditWikiPostProps) => {
  const dispatch = useAppDispatch();
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoWikiPost>
  >((state) => state.edoWikiPost);
  const { entities: edoSubjectList, loading: loadingSubject } = useAppSelector<
    EntityState<IEdoSubject>
  >((state) => state.edoSubject);
  const { entities: edoCourseYearList } = useAppSelector<EntityState<IEdoCourseYear>>(
    (state) => state.edoCourseYear
  );
  const [form, setForm] = useState<IEdoWikiPost>({ ...defaultValue, ...props.wikiPost });
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInput = createRef<HTMLInputElement>();
  const isNew = !props.wikiPost;
  const { wikiPost, open, onClose: handleClose } = props;

  useEffect(() => {
    dispatch(getEdoClasseList({ page: 0, size: 1000, sort: "id:ASC" }));
    dispatch(getEdoSubjectList({ page: 0, size: 1000, sort: "name:ASC" }));
  }, []);

  useEffect(() => {
    setForm({ ...defaultValue, ...wikiPost });
  }, [wikiPost]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (open && isNew) {
      setFiles([]);
      setForm({ ...defaultValue });
    }
  }, [open]);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const saveEntity = async (data?: {
    title?: string;
    years?: { id: number }[];
    subject?: IEdoSubject | null;
    educationalStage?: EdoEducationalStage;
  }) => {
    const payload = data ? { ...form, ...data } : form;
    if (isNew) {
      if (files.length) {
        setUploading(true);
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i], files[i].name);
        }
        const response = await axios.post<{ data: IEdoFile[] }>(
          "/files/upload",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setUploading(false);
        dispatch(
          createEntity({
            ...payload,
            file: { id: response.data.data[0].id },
          } as any)
        );
      }
    } else {
      dispatch(updateEntity({ ...payload } as any));
    }
  };

  const schema = z.object({
    title: z
      .string()
      .min(1, translate("edocendoApp.edoWikiPost.title") + " required"),
    yearIds: z.array(z.number()).min(1, "Anni required"),
    subjectId: z.number({
      required_error:
        translate("edocendoApp.edoWikiPost.subject") + " required",
    }),
    educationalStage: z.nativeEnum(EdoEducationalStage),
  });

  const selectFiles = () => {
    fileInput.current?.click();
  };

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiles([...Array.from(event.target.files || [])]);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? translate("edocendoApp.edoWikiPost.home.createLabel")
              : translate("edocendoApp.edoWikiPost.home.editLabel")}
          </DialogTitle>
        </DialogHeader>
        <p className="mb-4 text-sm text-[var(--color-mockup-text-light)]">
          Inserisci le informazioni del contenuto wiki. Puoi selezionare uno o più
          anni e uno o più istituti.
        </p>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form
            id="edit-wiki-post-form"
            schema={schema}
            onSubmit={(data) =>
              saveEntity({
                title: data.title,
                years: data.yearIds.map((id) => ({ id })),
                subject: data.subjectId
                  ? ({ id: data.subjectId } as IEdoSubject)
                  : null,
                educationalStage: data.educationalStage,
              })
            }
            defaultValues={{
              title: form.title ?? "",
              yearIds: form.years?.map((el) => el.id) ?? [],
              subjectId: form.subject?.id ?? 0,
              educationalStage:
                (form.educationalStage as EdoEducationalStage) ??
                EdoEducationalStage.PRIMARIA,
            }}
            className="flex flex-col gap-4"
          >
            <FormField
              name="title"
              label={translate("edocendoApp.edoWikiPost.title")}
            />
            <Controller
              name="yearIds"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                    Anni <span className="text-[var(--color-mockup-coral)]">*</span>
                  </label>
                  <select
                    multiple
                    value={field.value.map(String)}
                    onChange={(e) => {
                      const selected = Array.from(
                        e.target.selectedOptions,
                        (o) => o.value
                      ).map((v) => parseInt(v, 10));
                      field.onChange(selected);
                    }}
                    className={cn(
                      "flex min-h-[80px] w-full rounded-2xl border-2 border-[var(--color-mockup-border)] bg-surface px-3 py-2 text-sm focus:outline-none focus:border-primary focus:shadow-[0_4px_12px_rgba(18,181,170,0.15)] transition-all duration-200"
                    )}
                  >
                    {edoCourseYearList?.map((annoCorso) => (
                      <option key={annoCorso.id} value={annoCorso.id}>
                        {annoCorso.year}
                      </option>
                    )) ?? null}
                  </select>
                </div>
              )}
            />
            <Controller
              name="subjectId"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                    {translate("edocendoApp.edoWikiPost.subject")}{" "}
                    <span className="text-[var(--color-mockup-coral)]">*</span>
                  </label>
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(parseInt(v, 10))}
                    disabled={loadingSubject}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={translate("edocendoApp.edoWikiPost.subject")} />
                    </SelectTrigger>
                    <SelectContent>
                      {edoSubjectList?.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name}
                        </SelectItem>
                      )) ?? null}
                    </SelectContent>
                  </Select>
                  {loadingSubject && (
                    <Loader2 className="mt-1 h-4 w-4 animate-spin text-[var(--color-mockup-text-light)]" />
                  )}
                </div>
              )}
            />
            <Controller
              name="educationalStage"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                    {translate("edocendoApp.edoWikiPost.educationalStage")}{" "}
                    <span className="text-[var(--color-mockup-coral)]">*</span>
                  </label>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={(v) =>
                      field.onChange(v as EdoEducationalStage)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        EdoEducationalStage.INFANZIA,
                        EdoEducationalStage.PRIMARIA,
                        EdoEducationalStage.SECONDARIA_I,
                        EdoEducationalStage.SECONDARIA_II,
                      ].map((stage) => (
                        <SelectItem key={stage} value={stage}>
                          <Translate
                            contentKey={`edocendoApp.EdoEducationalStage.${stage}`}
                          />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            {isNew && (
              <>
                {files.length > 0 && (
                  <List className="rounded-2xl border-2 border-[var(--color-mockup-border)] divide-y divide-[var(--color-mockup-border)]">
                    {files.map((file, index) => (
                      <ListItem
                        key={index}
                        className="flex items-center gap-3 px-4 py-2.5"
                      >
                        {getFileAvatar(file.type)}
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--color-mockup-text)]">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="rounded-full p-1 text-[var(--color-mockup-text-light)] transition-colors hover:bg-[var(--color-mockup-coral)]/10 hover:text-[var(--color-mockup-coral)]"
                          aria-label="Rimuovi file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={selectFiles}
                >
                  <Paperclip className="h-4 w-4" />
                  Aggiungi file
                </Button>
                <input
                  type="file"
                  ref={fileInput}
                  name="files"
                  multiple
                  hidden
                  onChange={handleFiles}
                />
              </>
            )}
          </Form>
        )}
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            <Translate contentKey="entity.action.cancel">Cancel</Translate>
          </Button>
          <Button
            type="submit"
            form="edit-wiki-post-form"
            disabled={updating || (isNew && !form.file && !files.length)}
          >
            {updating || uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isNew ? (
              translate("entity.action.create")
            ) : (
              translate("entity.action.save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditWikiPost;
